// src/components/AlbumPageTemplates/RichTextEditor.jsx
// FIXED VERSION - Corrects RTL text and selection issues

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const EditorContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
  flex-wrap: wrap;
`;

const ToolButton = styled.button.attrs({ type: 'button' })`
  width: 32px;
  height: 32px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background: ${props => props.active ? '#e0e7ff' : 'white'};
  color: ${props => props.active ? '#3730a3' : '#333'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#c7d2fe' : '#f5f5f5'};
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ColorButton = styled(ToolButton)`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
    width: 16px;
    height: 3px;
    background: ${props => props.color};
    border-radius: 2px;
  }
`;

const SizeSelector = styled.select`
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background: white;
  color: #111;            /* ← force dark text so it’s readable */
  cursor: pointer;
  font-size: 12px;
`;

const TextArea = styled.div`
  min-height: 200px;
  padding: 12px;
  font-family: 'Georgia', serif;
  font-size: 1em;
  line-height: 1.6;
  color: #444;
  white-space: pre-wrap; /* keep <br> visible across browsers */
  outline: none;
  overflow-y: auto;
  
  /* FIX #1: Force left-to-right text direction */
  direction: ltr;
  text-align: left;
  
  /* FIX #2: Ensure text is selectable */
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  
  &:focus {
    background: #fafafa;
  }
  
  /* FIX #3: Placeholder styling */
  &:empty:before {
    content: attr(data-placeholder);
    color: #999;
    font-style: italic;
    pointer-events: none;
  }
  
  /* Support for formatted elements */
  strong { font-weight: bold; }
  em { font-style: italic; }
  u { text-decoration: underline; }
  mark { background: #fef3c7; padding: 0 2px; }
`;

export default function RichTextEditor({ 
  value, 
  onChange, 
  placeholder = "Write your story here..." 
}) {
  const editorRef = useRef(null);
  const [selectedText, setSelectedText] = useState('');
  const isInitialMount = useRef(true);
  
  // FIX #4: Only set initial content, don't re-render on every change
  useEffect(() => {
    if (isInitialMount.current && editorRef.current) {
      editorRef.current.innerHTML = value || '';
      isInitialMount.current = false;
    }
  }, [value]);
  
  // Apply formatting command
  const applyFormat = (command, value = null) => {
    // Focus the editor first
    if (editorRef.current) {
      editorRef.current.focus();
    }
    
    document.execCommand(command, false, value);
    
    if (editorRef.current) {
      // Trigger onChange with the new HTML content
      onChange(editorRef.current.innerHTML);
    }
  };
  
  // Handle text selection
  const handleSelection = () => {
    const selection = window.getSelection();
    setSelectedText(selection.toString());
  };
  
  // Insert color span
  const applyColor = (color) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && selection.toString()) {
      applyFormat('foreColor', color);
    }
  };
  
  // Handle paste to strip unwanted formatting
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };
  
  // Handle input changes
  const handleInput = (e) => {
    onChange(e.target.innerHTML);
  };

    

  // Make Enter behave consistently
  const handleKeyDown = (e) => {
    // Shift+Enter => soft break (<br>)
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertHTML', false, '<br>');
      onChange(editorRef.current.innerHTML);
      return;
    }
    // Enter => paragraph break
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Use a blank paragraph so the caret lands on the new line
      document.execCommand('insertHTML', false, '<p><br></p>');
      onChange(editorRef.current.innerHTML);
    }
  };
  
  return (
    <EditorContainer>
      <Toolbar>
        {/* Text formatting */}
        <ToolButton 
          onClick={() => applyFormat('bold')}
          title="Bold (Ctrl+B)"
        >
          B
        </ToolButton>
        
        <ToolButton 
          onClick={() => applyFormat('italic')}
          title="Italic (Ctrl+I)"
          style={{ fontStyle: 'italic' }}
        >
          I
        </ToolButton>
        
        <ToolButton 
          onClick={() => applyFormat('underline')}
          title="Underline (Ctrl+U)"
          style={{ textDecoration: 'underline' }}
        >
          U
        </ToolButton>
        
        <ToolButton 
          onClick={() => applyFormat('hiliteColor', '#fef3c7')}
          title="Highlight"
        >
          H
        </ToolButton>
        
        <div style={{ width: '1px', background: '#e0e0e0', margin: '0 4px' }} />
        
        {/* Font size */}
        <SizeSelector 
          onChange={(e) => applyFormat('fontSize', e.target.value)}
          defaultValue="3"
        >
          <option value="1">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="7">X-Large</option>
        </SizeSelector>
        
        <div style={{ width: '1px', background: '#e0e0e0', margin: '0 4px' }} />
        
        {/* Colors */}
        <ColorButton 
          onClick={() => applyColor('#000000')}
          color="#000000"
          title="Black"
        >
          A
        </ColorButton>
        
        <ColorButton 
          onClick={() => applyColor('#dc2626')}
          color="#dc2626"
          title="Red"
        >
          A
        </ColorButton>
        
        <ColorButton 
          onClick={() => applyColor('#2563eb')}
          color="#2563eb"
          title="Blue"
        >
          A
        </ColorButton>
        
        <ColorButton 
          onClick={() => applyColor('#16a34a')}
          color="#16a34a"
          title="Green"
        >
          A
        </ColorButton>
        
        <ColorButton 
          onClick={() => applyColor('#ca8a04')}
          color="#ca8a04"
          title="Gold"
        >
          A
        </ColorButton>
        
        <div style={{ width: '1px', background: '#e0e0e0', margin: '0 4px' }} />
        
        {/* Clear formatting */}
        <ToolButton 
          onClick={() => applyFormat('removeFormat')}
          title="Clear formatting"
        >
          ✕
        </ToolButton>
      </Toolbar>
      
      <TextArea
        ref={editorRef}
        contentEditable={true}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        onMouseUp={handleSelection}
        onKeyUp={handleSelection}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </EditorContainer>
  );
}